import React, { useCallback, useState } from 'react';
import Lottie from 'lottie-react';
import UnityGameDockApp from './UnityGameDockApp';
import clapAnimation from '../images/lottie/breakout-clap.json';

const DEFAULT_COOLDOWN_MS = 1800;

export interface BreakoutClapDockProps {
  disabled?: boolean;
  onClap: () => Promise<void>;
  clapCooldownMs?: number;
  dataTourId?: string;
}

export const BreakoutClapDock: React.FC<BreakoutClapDockProps> = ({
  disabled = false,
  onClap,
  clapCooldownMs = DEFAULT_COOLDOWN_MS,
  dataTourId,
}) => {
  const [pending, setPending] = useState(false);
  const [errorFlash, setErrorFlash] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const inCooldown = Date.now() < cooldownUntil;
  const canClap = Boolean(onClap) && !disabled && !pending && !inCooldown;

  const handleClapClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canClap) return;
      setPending(true);
      try {
        await onClap();
        setCooldownUntil(Date.now() + clapCooldownMs);
      } catch {
        setErrorFlash(true);
        window.setTimeout(() => setErrorFlash(false), 2000);
      } finally {
        setPending(false);
      }
    },
    [canClap, clapCooldownMs, onClap]
  );

  const title = !onClap || disabled ? 'Clap (connect to chat in this room)' : 'Send applause to everyone in this room';

  return (
    <UnityGameDockApp
      dataTourId={dataTourId}
      ariaLabel={title}
      label="Clap"
      icon={<span className="text-2xl leading-none">👏</span>}
      disabled={!canClap}
      wrapperClassName={errorFlash ? 'ring-2 ring-red-400 rounded-xl' : ''}
      tileClassName="bg-gradient-to-br from-emerald-600 to-green-800"
      onClick={handleClapClick}
    />
  );
};

export interface ClapBurst {
  id: string;
  displayName: string;
  leftPct: number;
  topPct: number;
  dx: string;
  dy: string;
}

export interface BreakoutClapOverlayProps {
  bursts: ClapBurst[];
}

/**
 * Bundled `breakout-clap.json`: Google Noto Emoji Lottie for U+1F44F (clap).
 * @see https://fonts.gstatic.com/s/e/notoemoji/latest/1f44f/lottie.json (Apache-2.0)
 */
const clapLottieStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  maxWidth: '4.25rem',
  maxHeight: '4.25rem',
  backgroundColor: 'transparent',
};

/**
 * Full-viewport floating clap bursts with display name (pointer-events none). z-index below dock tray.
 */
export const BreakoutClapOverlay: React.FC<BreakoutClapOverlayProps> = ({ bursts }) => {
  if (bursts.length === 0) return null;

  return (
    <div
      className="unity-clap-overlay fixed inset-0 overflow-hidden pointer-events-none"
      aria-hidden
    >
      {bursts.map((b) => {
        const label = (b.displayName || 'Someone').trim() || 'Someone';
        return (
          <div
            key={b.id}
            className="unity-clap-anchor absolute"
            style={{
              left: `${b.leftPct}%`,
              top: `${b.topPct}%`,
              transform: 'translate(-50%, -50%)',
            }}
          >
            <div
              className="unity-clap-float unity-clap-burst flex max-w-[min(14rem,calc(100vw-3rem))] flex-col items-center gap-1 rounded-2xl border border-emerald-800/40 bg-gradient-to-b from-emerald-600/90 to-green-800/92 px-3 py-2 shadow-2xl shadow-emerald-950/40 backdrop-blur-md"
              style={
                {
                  '--unity-clap-dx': b.dx,
                  '--unity-clap-dy': b.dy,
                } as React.CSSProperties
              }
            >
              <div className="relative flex h-[4.25rem] w-[4.25rem] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-emerald-500/35">
                <div className="unity-clap-lottie-root motion-reduce:hidden h-full w-full [&_svg]:mx-auto [&_svg]:block [&_svg]:max-h-full [&_svg]:max-w-full [&_svg]:overflow-visible">
                  <Lottie
                    animationData={clapAnimation}
                    loop
                    autoplay
                    style={clapLottieStyle}
                  />
                </div>
                <span
                  className="hidden select-none text-4xl leading-none drop-shadow-md motion-reduce:block"
                  aria-hidden
                >
                  👏
                </span>
              </div>
              <span className="w-full truncate text-center text-[11px] font-semibold leading-tight tracking-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
